import { useState } from 'react';
import Tooltip from '@mui/material/Tooltip';
import DashboardCard, { EmptyState } from './DashboardCard';
import CardGrid from './CardGrid';
import { CARD_FILL_MIN_WIDTH, type CardColumn } from './cardColumns';
import { useGlobalStore } from '../../stores/globalStore';
import TrayLineAssignDialog from '../people/TrayLineAssignDialog';
import type { DashboardTrayLineWarning } from '../../types/dashboard';
import { useDashboardNav, RESIDENT_TAB } from './useDashboardNav';

const columns: CardColumn<DashboardTrayLineWarning>[] = [
  { dataField: 'fullName', caption: 'Name', sizing: 'fill', sortOrder: 'asc' },
  { dataField: 'dietOrder', caption: 'Diet Order', sizing: 'fill', minWidth: CARD_FILL_MIN_WIDTH },
];

interface Props {
  data: DashboardTrayLineWarning[];
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
}

export default function TrayLineWarningsCard({ data, ...dragProps }: Props) {
  const { rowLinkProps } = useDashboardNav();
  const facilityId = useGlobalStore((s) => s.facilityId);
  // Double-click a warning row → open the full Assign Tray Line
  // dialog (the same one the patient profile's Tray Line tab uses)
  // scoped to that patient. Same props the profile passes:
  // `facilityId`, focused `patientId`, and an `onClose`. The dialog
  // handles its own save + audit-log path.
  const [assignPatientId, setAssignPatientId] = useState<number | null>(null);
  // Info icon rendered in the card header (right-aligned via
  // `DashboardCard`'s `headerRight` slot) so the double-click
  // affordance is discoverable — the grid itself doesn't hint at
  // it since we removed single-click navigation in the earlier
  // cleanup pass.
  const infoIcon = (
    <Tooltip title="Double click row to assign tray line" arrow>
      <i
        className="bi bi-info-circle"
        style={{
          color: 'var(--text-secondary, #64748b)',
          fontSize: 14,
          cursor: 'help',
        }}
      />
    </Tooltip>
  );
  return (
    <DashboardCard severity="warning" icon="bi-exclamation-triangle" title="Tray Line Warnings" count={data.length} headerRight={infoIcon} {...dragProps}>
      {data.length === 0 ? (
        <EmptyState message="No tray line warnings" />
      ) : (
        <>
          <CardGrid<DashboardTrayLineWarning>
            {...rowLinkProps<DashboardTrayLineWarning>((r) => r.pKey_Patient, RESIDENT_TAB.trayLine)}
            // Double-click (or Enter on the keyboard row) assigns the tray line here.
            onRowDblClick={(e) => setAssignPatientId(e.data.pKey_Patient)}
            ariaLabel="Tray Line Warnings"
            dataSource={data}
            columns={columns}
            keyExpr="pKey_Patient"
          />
          {assignPatientId != null && (
            <TrayLineAssignDialog
              open
              facilityId={facilityId}
              patientId={assignPatientId}
              onClose={() => setAssignPatientId(null)}
            />
          )}
        </>
      )}
    </DashboardCard>
  );
}
